import { Box, Paper, Typography, Link } from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import Fonts from './Fonts';
import { themeColors } from '../styles/theme';
import TopNav from './TopNav';
import AdSense from './AdSense';
import CoverageStats from './CoverageStats';
import ScrollIndicator from './ScrollIndicator';
import { Link as RouterLink } from 'react-router-dom';
import { Footer } from './Footer';

interface AboutProps {
    setTitle: Dispatch<SetStateAction<string>>;
    setShowBurgerMenuIcon: Dispatch<SetStateAction<boolean>>;
}

const About = ({
   setTitle,
   setShowBurgerMenuIcon
}: AboutProps) => {
    const [localTitle, setLocalTitle] = useState<string>("About");

    useEffect(() => {
        setTitle("About");
        setLocalTitle("About");
        setShowBurgerMenuIcon(true);
    }, // eslint-disable-next-line
        []);

    return (
        <>
        <Fonts />
        <TopNav title={localTitle} />

        <Box 
            sx={{
                width: '100%',
                maxWidth: '900px',
                margin: '0 auto',
                padding: { xs: '0px 16px 16px', sm: '0px 24px 20px' },
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                mt: 3
            }}
        >
            {/* Text Content Box */}
            <Paper
                elevation={2}
                sx={{ 
                    width: '100%',
                    p: 3,
                    borderRadius: 4,
                    background: themeColors.paperYellow,
                    backdropFilter: 'blur(16px)',
                    border: `1px solid ${themeColors.orange}20`,
                }}
            >
                <Typography
                    variant="body1"
                    sx={{
                        fontSize: { xs: '0.95rem', sm: '1rem' },
                        lineHeight: 1.6,
                        color: themeColors.darkBackground,
                        textAlign: 'justify',
                        fontFamily: "'Roboto', sans-serif",
                        marginBottom: '16px'
                    }}
                >
                    We're building a comprehensive guide to the UK's sunniest pub gardens. Our mission is to help you find the perfect spot to enjoy a drink in the sunshine, powered by real-time sunlight calculations that can tell you if a pub garden is currently sunny, and even give you the best time of day to visit and the best place to sit.
                </Typography>

                <Typography
                    variant="body1"
                    sx={{
                        fontSize: { xs: '0.95rem', sm: '1rem' },
                        lineHeight: 1.6,
                        color: themeColors.darkBackground,
                        textAlign: 'justify',
                        fontFamily: "'Roboto', sans-serif",
                        marginBottom: '16px'
                    }}
                >
                    While we fetch pubs from Google for the most up to date results, we rely on our community to help map the gardens so we can do the right calculations. Every garden submission helps make our data more accurate and helps others find their perfect sunny spot. Whether you're a regular pub-goer or just passing through, your contribution makes a difference.
                </Typography>

                <Typography
                    variant="body1"
                    sx={{
                        fontSize: { xs: '0.95rem', sm: '1rem' },
                        lineHeight: 1.6,
                        color: themeColors.darkBackground,
                        textAlign: 'justify',
                        fontFamily: "'Roboto', sans-serif",
                        marginBottom: '16px'
                    }}
                >
                    Join our community of pub enthusiasts and help us build the most accurate guide to the UK's sunniest spots to have a pint. Your local knowledge is invaluable in creating a better experience for everyone. Please review our <Link component={RouterLink} to="/terms-of-use" sx={{ color: themeColors.blue }}>Terms of Use</Link> and <Link component={RouterLink} to="/privacy-policy" sx={{ color: themeColors.blue }}>Privacy Policy</Link> for information on how we handle your data and the rules for using our service.
                </Typography>

                <Typography
                    variant="body1"
                    sx={{
                        fontSize: { xs: '0.95rem', sm: '1rem' },
                        lineHeight: 1.6,
                        color: themeColors.darkBackground,
                        textAlign: 'justify',
                        fontFamily: "'Roboto', sans-serif",
                        marginBottom: '0'
                    }}
                >
                    If you have any questions or feedback, please contact us at <a href="mailto:hello@sun-tap.com">info@sun-tap.com</a>.
                </Typography>
            </Paper>

            {/* Advertisement Section - Not in a Paper */}
            <Box sx={{ width: '100%', my: 1, borderRadius: '40px' }}>
                <AdSense
                    client="ca-pub-3335995580070945"
                    slot="8169021635"
                    format="auto"
                    responsive="true"
                    width="100%"
                    height="250px"
                />
            </Box>

            {/* Coverage Statistics Section in its own Paper */}
            <Paper
                elevation={2}
                sx={{
                    width: '100%',
                    p: { xs: 1.5, sm: 2, md: 3 },
                    borderRadius: 4,
                    background: themeColors.paperYellow,
                    backdropFilter: 'blur(16px)',
                    border: `1px solid ${themeColors.orange}20`,
                    overflow: 'hidden', // Prevent table from spilling out
                }}
            >
                {/* Wrapper for the CoverageStats to help with table sizing */}
                <Box
                    sx={{
                        width: '100%',
                        overflow: 'hidden',
                        '& .MuiTableContainer-root': {
                            width: '100%',
                            maxWidth: '100%',
                        },
                        '& .MuiTable-root': {
                            tableLayout: 'fixed', // Fixed table layout helps with column width
                        },
                        '& .MuiTableCell-root': {
                            padding: { xs: '6px 4px', sm: '10px 8px' }, // Further reduce padding on xs screens
                            fontSize: { xs: '0.7rem', sm: '0.8rem' }, // Smaller font on mobile
                            wordBreak: 'break-word', // Handles long text better
                        },
                        '& .MuiTooltip-popper': {
                            zIndex: 1400 // Ensure tooltips are above other elements
                        }
                    }}
                >
                    <CoverageStats />
                </Box>
            </Paper>

            {/* FAQ Section */}
            <Paper
                elevation={2}
                sx={{
                    width: '100%',
                    p: 3,
                    borderRadius: 4,
                    background: themeColors.paperYellow,
                    backdropFilter: 'blur(16px)',
                    border: `1px solid ${themeColors.orange}20`
                }}
            >
                <Typography
                    variant="h2"
                    sx={{
                        fontSize: '1.5rem',
                        fontFamily: "'DM-Sans', sans-serif",
                        color: themeColors.darkBackground,
                        mb: 3
                    }}
                >
                    Frequently Asked Questions
                </Typography>

                {/* FAQ Schema Markup */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": [
                            {
                                "@type": "Question",
                                "name": "How does Suntap calculate sun exposure?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "We use 3D geometry and raycasting technology combined with precise sun position calculations to determine which parts of a pub garden will be in sunshine at different times of day. Our system accounts for buildings, walls, and other structures that might cast shadows, with 15-minute granularity throughout the day."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "Which cities are currently supported?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "We currently focus on London, Bristol and Brighton, but our technology works for any major city in the UK where 3D mapping data is available. We're constantly expanding our coverage."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "How accurate is the sun prediction?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Our predictions are highly accurate based on building geometry and sun position. Points are placed every 15cm across the garden in a regular grid pattern, and we calculate shadows using real 3D building data. However, we don't account for weather conditions or temporary structures like umbrellas that may not be included in the 3D data."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "Does Suntap account for weather forecasts?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Currently, Suntap calculates sun exposure based solely on 3D geometry without weather prediction. Our calculations show where sun would hit in clear conditions. Future versions may integrate weather APIs to provide more comprehensive predictions."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "Can I add a pub garden that's missing from your database?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Yes! We welcome user contributions. You can add missing pub gardens by drawing them through our app. Simply use the 'Submit Garden' options in the app."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "How do I know which parts of a garden will be sunny?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Our 3D visualisation shows exactly which parts of a garden will be in sun or shade at different times. Yellow markers indicate sunny areas, while dark markers show shaded areas. You can use the time slider to see how the sun moves throughout the day."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "Why does my favorite pub show as 'not supported'?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Some locations may not be supported by the 3D mapping data we use. This can happen in areas where 3D building data is incomplete or unavailable. We're constantly improving our coverage, so check back soon!"
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "Can I use Suntap when traveling abroad?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Currently, we're focused on UK locations, but our technology can work internationally in areas with good 3D mapping data. We plan to expand to more international cities in the future."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "Does the app work offline?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Suntap is a Progressive Web App (PWA) with some offline capabilities, but full functionality requires an internet connection to load pub data and 3D visualisations."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "How can I share a sunny pub with friends?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Each pub has sharing options including QR code generation, WhatsApp integration, and native sharing mechanisms. Just open the pub details and tap the share button."
                                }
                            }
                        ]
                    })}
                </script>

                {/* FAQ questions and answers */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box>
                        <Typography
                            variant="h3"
                            sx={{
                                fontSize: '1.2rem',
                                fontWeight: 600,
                                color: themeColors.darkBackground,
                                mb: 1
                            }}
                        >
                            How does Suntap calculate sun exposure?
                        </Typography>
                        <Typography variant="body1">
                            We use 3D geometry and raycasting technology combined with precise sun position calculations to determine which parts of a pub garden will be in sunshine at different times of day. Our system accounts for buildings, walls, and other structures that might cast shadows, with 15-minute granularity throughout the day. Points are placed every 15cm across the garden in a regular grid pattern to ensure accurate calculations.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography
                            variant="h3"
                            sx={{
                                fontSize: '1.2rem',
                                fontWeight: 600,
                                color: themeColors.darkBackground,
                                mb: 1
                            }}
                        >
                            Which cities are currently supported?
                        </Typography>
                        <Typography variant="body1">
                            We currently focus on London, Bristol and Brighton, but our technology works for any major city in the UK where 3D mapping data is available. We're constantly expanding our coverage to include more locations throughout the UK.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography
                            variant="h3"
                            sx={{
                                fontSize: '1.2rem',
                                fontWeight: 600,
                                color: themeColors.darkBackground,
                                mb: 1
                            }}
                        >
                            How accurate is the sun prediction?
                        </Typography>
                        <Typography variant="body1">
                            Our predictions are highly accurate based on building geometry and sun position. The sun calculations take into account the exact day and moment, adjusting for seasonal changes in the sun's position. However, we don't account for weather conditions (like cloudy days) or temporary structures like umbrellas that may not be included in the 3D data.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography
                            variant="h3"
                            sx={{
                                fontSize: '1.2rem',
                                fontWeight: 600,
                                color: themeColors.darkBackground,
                                mb: 1
                            }}
                        >
                            Does Suntap account for weather forecasts?
                        </Typography>
                        <Typography variant="body1">
                            Currently, Suntap calculates sun exposure based solely on 3D geometry without weather prediction. Our calculations show where sun would hit in clear conditions. Future versions may integrate weather APIs to provide more comprehensive predictions.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography
                            variant="h3"
                            sx={{
                                fontSize: '1.2rem',
                                fontWeight: 600,
                                color: themeColors.darkBackground,
                                mb: 1
                            }}
                        >
                            How do I know which parts of a garden will be sunny?
                        </Typography>
                        <Typography variant="body1">
                            Our 3D visualisation shows exactly which parts of a garden will be in sun or shade at different times. Yellow markers indicate sunny areas, while dark markers show shaded areas. You can use the time slider to see how the sun moves throughout the day, helping you pick the perfect spot for your visit.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography
                            variant="h3"
                            sx={{
                                fontSize: '1.2rem',
                                fontWeight: 600,
                                color: themeColors.darkBackground,
                                mb: 1
                            }}
                        >
                            Why does my favorite pub show as 'not supported'?
                        </Typography>
                        <Typography variant="body1">
                            Some locations may not be supported by the 3D mapping data we use. This can happen in areas where 3D building data is incomplete or unavailable. We're constantly improving our coverage, so check back soon!
                        </Typography>
                    </Box>

                    <Box>
                        <Typography
                            variant="h3"
                            sx={{
                                fontSize: '1.2rem',
                                fontWeight: 600,
                                color: themeColors.darkBackground,
                                mb: 1
                            }}
                        >
                            Can I use Suntap when traveling abroad?
                        </Typography>
                        <Typography variant="body1">
                            Currently, we're focused on UK locations, but our technology can work internationally in areas with good 3D mapping data. We plan to expand to more international cities in the future. The application works through timezone adjustments, so the sun calculations would still be accurate.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography
                            variant="h3"
                            sx={{
                                fontSize: '1.2rem',
                                fontWeight: 600,
                                color: themeColors.darkBackground,
                                mb: 1
                            }}
                        >
                            Does the app work offline?
                        </Typography>
                        <Typography variant="body1">
                            Suntap is a Progressive Web App (PWA) with some offline capabilities, but full functionality requires an internet connection to load pub data and 3D visualisations. You can install it on your home screen for quick access.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography
                            variant="h3"
                            sx={{
                                fontSize: '1.2rem',
                                fontWeight: 600,
                                color: themeColors.darkBackground,
                                mb: 1
                            }}
                        >
                            How can I share a sunny pub with friends?
                        </Typography>
                        <Typography variant="body1">
                            Each pub has sharing options including QR code generation, WhatsApp integration, and native sharing mechanisms. Just open the pub details and tap the share button to let your friends know about that perfect sunny spot you've discovered.
                        </Typography>
                    </Box>
                </Box>
            </Paper>
        </Box>

        {/* Add Footer */}
        <Box sx={{ px: '5%' }}>
            <Footer />
        </Box>

        <ScrollIndicator
            forceVisible={true}
            iconColor={themeColors.orange}
            backgroundColor="rgba(255, 255, 255, 0.8)"
        />
        </>
    );
};

export default About;